// slice.ts
import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import moment from 'moment';
import { createAction, geneAsyncAction } from 'services/actionConfigs';
import { ApiStatus, PaginatedList, PaginationResponse } from 'services/commonTypes';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import {
  IDateRangeParams,
  IInventoryProduct,
  IInventorySearchParams,
  IInventorySummary,
  IOutOfStockItem,
  IOutOfStockSearchParams,
  IPaymentSummary,
  IPaymentTypeItem,
  IPaymentTypeParams,
  IProductDetailParams,
  IProductDetailSummary,
  IProductDetailSummaryParams,
  IProductSale,
  IProductSaleChart,
  IProductSaleDetail,
  IProductSaleRanking,
  IProductSaleRankingParams,
  IReportSummary,
  ISummaryProductSale,
  ISummaryProductsParams,
  TimeFilter,
} from './types';

const featureName = 'report';

// Types
type SliceState = {
  summary: {
    status: ApiStatus;
    error?: string;
    data?: IReportSummary;
  };
  paymentSummary: {
    status: ApiStatus;
    error?: string;
    data?: IPaymentSummary;
  };
  paymentType: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<IPaymentTypeItem>;
    searchParams?: IPaymentTypeParams;
  };
  inventorySummary: {
    status: ApiStatus;
    error?: string;
    data?: IInventorySummary;
  };
  productDetailSummary: {
    status: ApiStatus;
    error?: string;
    data?: IProductDetailSummary;
    searchParams?: IProductDetailSummaryParams;
  };
  inventory: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<IInventoryProduct>;
    searchParams?: IInventorySearchParams;
  };
  outOfStock: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<IOutOfStockItem>;
    searchParams?: IOutOfStockSearchParams;
  };
  summaryProducts: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<IProductSale>;
    searchParams?: ISummaryProductsParams;
  };
  productDetail: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<IProductSaleDetail>;
    searchParams?: IProductDetailParams;
  };

  // Product sale
  productSale: PaginationResponse<IProductSaleRanking> | null;
  summaryProductSale: ISummaryProductSale | null;
  productSaleChart: IProductSaleChart[] | null;
};

// Actions
const createAsync = geneAsyncAction(featureName);
const api = {
  get: {
    summary: createAsync<IDateRangeParams, IReportSummary>('getSummary'),
    summaryPaymentType: createAsync<IDateRangeParams, IPaymentSummary>(
      'getSummaryPaymentType'
    ),
    summaryInventory: createAsync<IDateRangeParams, IInventorySummary>(
      'getSummaryInventory'
    ),
    summaryProductDetail: createAsync<
      IProductDetailSummaryParams,
      IProductDetailSummary
    >('getSummaryProductDetail'),
    inventory: createAsync<
      IInventorySearchParams,
      PaginatedList<IInventoryProduct>
    >('getInventory'),
    outOfStock: createAsync<
      IOutOfStockSearchParams,
      PaginatedList<IOutOfStockItem>
    >('getOutOfStock'),
    summaryProducts: createAsync<
      ISummaryProductsParams,
      PaginatedList<IProductSale>
    >('getSummaryProducts'),
    productDetail: createAsync<
      IProductDetailParams,
      PaginatedList<IProductSaleDetail>
    >('getProductDetail'),
    paymentType: createAsync<
      IPaymentTypeParams,
      PaginatedList<IPaymentTypeItem>
    >('getPaymentType'),

    //Product sale
    productSale: createAsync<IProductSaleRankingParams>('productSale'),
    summaryProductSale: createAsync<IDateRangeParams>('summaryProductSale'),
    productSaleChart: createAsync<{ type: TimeFilter }>('productSaleChart')
  },
  reset: {
    searchParams: {
      revenue: createAction<IDateRangeParams>('resetSearchParamsRevenue'),
      inventory: createAction<IDateRangeParams>('resetSearchParamsInventory'),
      paymentType: createAction<IDateRangeParams>(
        'resetSearchParamsPaymentType'
      ),
      productDetail: createAction<IProductDetailParams>(
        'resetSearchParamsProductDetail'
      ),
    },
  },
};

// Selectors
const getState = (state: RootState) => state[featureName];
const select = (key: keyof SliceState) =>
  useAppSelector((state) => get(getState(state), key));

export const use = {
  summary: () => select('summary'),
  paymentSummary: () => select('paymentSummary'),
  inventorySummary: () => select('inventorySummary'),
  productDetailSummary: () => select('productDetailSummary'),
  inventory: () => select('inventory'),
  outOfStock: () => select('outOfStock'),
  summaryProducts: () => select('summaryProducts'),
  productDetail: () => select('productDetail'),
  paymentType: () => select('paymentType'),

  productSale: () => select('productSale') as SliceState['productSale'],
  summaryProductSale: () => select('summaryProductSale') as SliceState['summaryProductSale'],
  productSaleChart: () => select('productSaleChart') as SliceState['productSaleChart']

};

// Slice
const slice = createSlice({
  name: featureName,
  initialState: {
    summary: { status: undefined },
    paymentSummary: { status: undefined },
    inventorySummary: { status: undefined },
    productDetailSummary: {
      status: undefined,
      searchParams: {
        productId: '',
        startDate: moment().format('MM-DD-YYYY'),
        endDate: moment().format('MM-DD-YYYY'),
      },
    },
    inventory: {
      status: undefined,
      searchParams: {
        startDate: moment().format('MM-DD-YYYY'),
        endDate: moment().format('MM-DD-YYYY'),
        page: 1,
        size: 20,
      },
    },
    outOfStock: {
      status: undefined,
      searchParams: {
        keySearch: '',
        page: 1,
        size: 20,
      },
    },
    summaryProducts: {
      status: undefined,
      searchParams: {
        startDate: moment().format('MM-DD-YYYY'),
        endDate: moment().format('MM-DD-YYYY'),
        page: 1,
        size: 20,
      },
    },
    productDetail: {
      status: undefined,
      searchParams: {
        productId: '',
        startDate: moment().format('MM-DD-YYYY'),
        endDate: moment().format('MM-DD-YYYY'),
        page: 1,
        size: 20,
      },
    },
    printSummarySingleDate: { status: undefined },
    printSummaryRangeDate: { status: undefined },
    paymentType: {
      status: undefined,
      searchParams: {
        startDate: moment().format('MM-DD-YYYY'),
        endDate: moment().format('MM-DD-YYYY'),
        page: 1,
        size: 20,
      },
    },

    //Product sale
    productSale: null,
    summaryProductSale: null,
    productSaleChart: null
  } as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    // Summary
    builder
      .addCase(api.get.summary.fetch, (state) => {
        state.summary.status = 'fetching';
        state.summary.error = undefined;
      })
      .addCase(api.get.summary.success, (state, { payload }) => {
        state.summary.status = 'success';
        state.summary.data = payload;
      })
      .addCase(api.get.summary.fail, (state, { payload }) => {
        state.summary.status = 'error';
        state.summary.error = payload;
      });

    // Payment Summary
    builder
      .addCase(api.get.summaryPaymentType.fetch, (state) => {
        state.paymentSummary.status = 'fetching';
        state.paymentSummary.error = undefined;
      })
      .addCase(api.get.summaryPaymentType.success, (state, { payload }) => {
        state.paymentSummary.status = 'success';
        state.paymentSummary.data = payload;
      })
      .addCase(api.get.summaryPaymentType.fail, (state, { payload }) => {
        state.paymentSummary.status = 'error';
        state.paymentSummary.error = payload;
      });

    // Inventory Summary
    builder
      .addCase(api.get.summaryInventory.fetch, (state) => {
        state.inventorySummary.status = 'fetching';
        state.inventorySummary.error = undefined;
      })
      .addCase(api.get.summaryInventory.success, (state, { payload }) => {
        state.inventorySummary.status = 'success';
        state.inventorySummary.data = payload;
      })
      .addCase(api.get.summaryInventory.fail, (state, { payload }) => {
        state.inventorySummary.status = 'error';
        state.inventorySummary.error = payload;
      });

    // Inventory
    builder
      .addCase(api.get.inventory.fetch, (state, { payload }) => {
        state.inventory.status = 'fetching';
        state.inventory.error = undefined;
        state.inventory.searchParams = payload;
      })
      .addCase(api.get.inventory.success, (state, { payload }) => {
        state.inventory.status = 'success';
        state.inventory.data = payload;
      })
      .addCase(api.get.inventory.fail, (state, { payload }) => {
        state.inventory.status = 'error';
        state.inventory.error = payload;
      });

    // Product Detail Summary
    builder
      .addCase(api.get.summaryProductDetail.fetch, (state) => {
        state.productDetailSummary.status = 'fetching';
        state.productDetailSummary.error = undefined;
      })
      .addCase(api.get.summaryProductDetail.success, (state, { payload }) => {
        state.productDetailSummary.status = 'success';
        state.productDetailSummary.data = payload;
      })
      .addCase(api.get.summaryProductDetail.fail, (state, { payload }) => {
        state.productDetailSummary.status = 'error';
        state.productDetailSummary.error = payload;
      });

    // Out of Stock
    builder
      .addCase(api.get.outOfStock.fetch, (state, { payload }) => {
        state.outOfStock.status = 'fetching';
        state.outOfStock.error = undefined;
        state.outOfStock.searchParams = payload;
      })
      .addCase(api.get.outOfStock.success, (state, { payload }) => {
        state.outOfStock.status = 'success';
        state.outOfStock.data = payload;
      })
      .addCase(api.get.outOfStock.fail, (state, { payload }) => {
        state.outOfStock.status = 'error';
        state.outOfStock.error = payload;
      });

    // Summary Products
    builder
      .addCase(api.get.summaryProducts.fetch, (state, { payload }) => {
        state.summaryProducts.status = 'fetching';
        state.summaryProducts.error = undefined;
        state.summaryProducts.searchParams = payload;
      })
      .addCase(api.get.summaryProducts.success, (state, { payload }) => {
        state.summaryProducts.status = 'success';
        state.summaryProducts.data = payload;
      })
      .addCase(api.get.summaryProducts.fail, (state, { payload }) => {
        state.summaryProducts.status = 'error';
        state.summaryProducts.error = payload;
      });

    // Product Detail
    builder
      .addCase(api.get.productDetail.fetch, (state, { payload }) => {
        state.productDetail.status = 'fetching';
        state.productDetail.error = undefined;
        state.productDetail.searchParams = payload;
      })
      .addCase(api.get.productDetail.success, (state, { payload }) => {
        state.productDetail.status = 'success';
        state.productDetail.data = payload;
      })
      .addCase(api.get.productDetail.fail, (state, { payload }) => {
        state.productDetail.status = 'error';
        state.productDetail.error = payload;
      });
    //Payment-type table data
    builder
      .addCase(api.get.paymentType.fetch, (state, { payload }) => {
        state.paymentType.status = 'fetching';
        state.paymentType.searchParams = payload;
      })
      .addCase(api.get.paymentType.success, (state, { payload }) => {
        state.paymentType.status = 'success';
        state.paymentType.data = payload;
      })
      .addCase(api.get.paymentType.fail, (state, { payload }) => {
        state.paymentType.status = 'error';
        state.paymentType.error = payload;
      });
    builder
      .addCase(api.reset.searchParams.paymentType, (state, { payload }) => {
        state.paymentType.searchParams = {
          ...payload,
          page: 1,
          size: 20,
        };
      })
      .addCase(api.reset.searchParams.inventory, (state, { payload }) => {
        state.inventory.searchParams = {
          ...payload,
          page: 1,
          size: 20,
        };
      })
      .addCase(api.reset.searchParams.revenue, (state, { payload }) => {
        state.summaryProducts.searchParams = {
          ...payload,
          page: 1,
          size: 20,
        };
      })
      .addCase(api.reset.searchParams.productDetail, (state, { payload }) => {
        state.productDetail.searchParams = {
          ...payload,
          page: 1,
          size: 20,
        };
        state.productDetailSummary.searchParams = {
          ...payload,
        };
      });

    //Product sale
    builder
      .addCase(api.get.productSale.success, (state, { payload }) => {
        state.productSale = payload;
      })
      .addCase(api.get.productSaleChart.success, (state, { payload }) => {
        state.productSaleChart = payload;
      })
      .addCase(api.get.summaryProductSale.success, (state, { payload }) => {
        state.summaryProductSale = payload;
      });
  },
});

export const actions = { ...slice.actions, api };
export const reducer = slice.reducer;
